import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';

export interface ExamEntry {
  id: string;
  tenantId: string;
  academicYearId: string;
  campusId: string;
  filiereId: string;
  groupeId: string;
  moduleElementId: string;
  professorId: string;
  roomId?: string;
  sessionTypeId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isExam: boolean;
  isRattrapage: boolean;
  isCombined: boolean;
  combinedGroupIds: string[];
  status: string;
  createdAt: string;
  updatedAt: string;
  // Relations
  campus?: { id: string; name: string };
  filiere?: { id: string; filiere: string; codeFiliere: string };
  groupe?: { id: string; name: string; niveau: number };
  moduleElement?: {
    id: string;
    name: string;
    module?: { id: string; name: string };
  };
  professor?: { id: string; firstName: string; lastName: string };
  sessionType?: { id: string; name: string; durationMinutes: number; color?: string };
  sessions?: ExamSession[];
}

export interface ExamSession {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  isCancelled: boolean;
}

export interface CreateExamEntryDto {
  academicYearId: string;
  campusId: string;
  filiereId: string;
  groupeId: string;
  moduleElementId: string;
  professorId?: string;
  isExternalProctor?: boolean;
  roomId?: string;
  sessionTypeId: string;
  examDate: string;
  startTime: string;
  endTime: string;
  isRattrapage?: boolean;
  /** User explicitly confirmed non-blocking warnings (outside exam period, weekend, holiday, max/day) */
  confirmWarnings?: boolean;
}

export interface CreateCommonExamDto {
  academicYearId: string;
  campusId: string;
  filiereId: string;
  groupIds: string[];
  moduleElementId: string;
  professorId?: string;
  isExternalProctor?: boolean;
  roomId?: string;
  sessionTypeId: string;
  examDate: string;
  startTime: string;
  endTime: string;
  isRattrapage?: boolean;
  /** User explicitly confirmed non-blocking warnings (outside exam period, weekend, holiday, max/day) */
  confirmWarnings?: boolean;
}

export interface UpdateExamEntryDto {
  professorId?: string;
  isExternalProctor?: boolean;
  roomId?: string;
  sessionTypeId?: string;
  examDate?: string;
  startTime?: string;
  endTime?: string;
  /** User explicitly confirmed non-blocking warnings (outside exam period, weekend, holiday, max/day) */
  confirmWarnings?: boolean;
}

export interface ExamFilter {
  academicYearId?: string;
  campusId?: string;
  filiereId?: string;
  groupeId?: string;
  professorId?: string;
  startDate?: string;
  endDate?: string;
  isRattrapage?: boolean;
}

export interface ExamConflictInfo {
  type: 'PROFESSOR' | 'ROOM' | 'GROUP' | 'MAX_EXAMS_PER_DAY' | 'OUTSIDE_EXAM_PERIOD' | 'WEEKEND' | 'HOLIDAY';
  severity: 'ERROR' | 'WARNING';
  message: string;
  details?: any;
}

export interface ExamConflictResult {
  hasConflicts: boolean;
  hasWarnings: boolean;
  errors: ExamConflictInfo[];
  warnings: ExamConflictInfo[];
}

export interface ExamPeriods {
  examPeriodType: 'SINGLE' | 'PER_SEMESTER';
  isUnifiedForAllCampuses: boolean;
  rattrapageEnabled: boolean;
  rattrapagePeriodType: 'SINGLE' | 'PER_SEMESTER';
  period1: { start: string | null; end: string | null };
  period2?: { start: string | null; end: string | null }; // Only for PER_SEMESTER
  rattrapage1: { start: string | null; end: string | null };
  rattrapage2?: { start: string | null; end: string | null }; // Only for PER_SEMESTER
}

export interface ExamCalendarData {
  month: number;
  year: number;
  exams: Record<string, ExamCalendarEntry[]>;
  examPeriods: ExamPeriods;
}

export interface ExamCalendarEntry {
  id: string;
  sessionId: string;
  moduleElement: { id: string; name: string; module?: { name: string } };
  professor: { id: string; firstName: string; lastName: string } | null;
  groupe: { id: string; name: string; niveau: number };
  filiere: { id: string; filiere: string; codeFiliere: string };
  room: { id: string; name: string; capacity: number } | null;
  sessionType: { id: string; name: string; durationMinutes: number } | null;
  startTime: string;
  endTime: string;
  examDate: string;
  isRattrapage: boolean;
  isCombined: boolean;
  combinedGroupIds: string[];
  isExternalProctor: boolean;
}

export interface RattrapageGroupInfo {
  groupe: { id: string; name: string; niveau: number };
  filiere: { id: string; filiere: string; codeFiliere: string; diplome: string; nombreAnnees: number };
  rattrapageStudentCount: number;
  moduleElements: { id: string; name: string; studentCount: number; semesterNumber?: number }[];
}

export interface ExamConfig {
  id: string | null;
  tenantId: string;
  academicYearId: string | null;
  campusId: string | null;
  filiereId: string | null;
  // Campus scope settings
  isUnifiedForAllCampuses: boolean;
  // Exam period configuration
  examPeriodType: 'SINGLE' | 'PER_SEMESTER';
  examPeriod1Start: string | null;
  examPeriod1End: string | null;
  examPeriod2Start: string | null;
  examPeriod2End: string | null;
  // Core settings
  maxExamsPerDayPerGroup: number;
  minDaysBetweenExams: number;
  enforceExamPeriod: boolean;
  allowWeekendExams: boolean;
  // Rattrapage settings
  rattrapageEnabled: boolean;
  rattrapagePeriodType: 'SINGLE' | 'PER_SEMESTER';
  rattrapagePeriod1Start: string | null;
  rattrapagePeriod1End: string | null;
  rattrapagePeriod2Start: string | null;
  rattrapagePeriod2End: string | null;
  rattrapageEligibility: string | null;
  isActive: boolean;
}

class ExamScheduleService {
  // CRUD operations
  async getExams(filter: ExamFilter): Promise<ExamEntry[]> {
    const params = new URLSearchParams();
    if (filter.academicYearId) params.append('academicYearId', filter.academicYearId);
    if (filter.campusId) params.append('campusId', filter.campusId);
    if (filter.filiereId) params.append('filiereId', filter.filiereId);
    if (filter.groupeId) params.append('groupeId', filter.groupeId);
    if (filter.professorId) params.append('professorId', filter.professorId);
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.isRattrapage !== undefined) params.append('isRattrapage', String(filter.isRattrapage));

    return apiGet<ExamEntry[]>(`/schedule/exams?${params.toString()}`);
  }

  async getExam(id: string): Promise<ExamEntry> {
    return apiGet<ExamEntry>(`/schedule/exams/${id}`);
  }

  async createExam(data: CreateExamEntryDto): Promise<ExamEntry> {
    return apiPost<ExamEntry>('/schedule/exams', data);
  }

  async createCommonExam(data: CreateCommonExamDto): Promise<ExamEntry> {
    return apiPost<ExamEntry>('/schedule/exams/common', data);
  }

  async updateExam(id: string, data: UpdateExamEntryDto): Promise<ExamEntry> {
    return apiPatch<ExamEntry>(`/schedule/exams/${id}`, data);
  }

  async deleteExam(id: string): Promise<void> {
    return apiDelete<void>(`/schedule/exams/${id}`);
  }

  // Conflict checking
  async checkConflicts(
    academicYearId: string,
    campusId: string,
    groupeId: string,
    professorId: string,
    examDate: string,
    startTime: string,
    endTime: string,
    roomId?: string,
    excludeEntryId?: string
  ): Promise<ExamConflictResult> {
    return apiPost<ExamConflictResult>('/schedule/exams/conflicts/check', {
      academicYearId,
      campusId,
      groupeId,
      // Omitted for external proctors — an empty string would fail backend UUID validation
      professorId: professorId || undefined,
      examDate,
      startTime,
      endTime,
      roomId,
      excludeEntryId,
    });
  }

  // Calendar view
  async getCalendar(
    academicYearId: string,
    campusId: string,
    month: number,
    year: number,
    filiereId?: string,
    groupeId?: string
  ): Promise<ExamCalendarData> {
    const params = new URLSearchParams();
    params.append('academicYearId', academicYearId);
    params.append('campusId', campusId);
    if (filiereId) params.append('filiereId', filiereId);
    if (groupeId) params.append('groupeId', groupeId);

    return apiGet<ExamCalendarData>(`/schedule/exams/calendar/${month}/${year}?${params.toString()}`);
  }

  // Exam periods
  async getExamPeriods(academicYearId: string, campusId?: string): Promise<ExamPeriods> {
    const params = new URLSearchParams();
    if (campusId) params.append('campusId', campusId);

    const queryString = params.toString() ? '?' + params.toString() : '';
    return apiGet<ExamPeriods>(`/schedule/exams/periods/${academicYearId}${queryString}`);
  }

  // Rattrapage groups
  async getGroupsWithRattrapageStudents(
    academicYearId: string,
    campusId?: string
  ): Promise<RattrapageGroupInfo[]> {
    const params = new URLSearchParams();
    params.append('academicYearId', academicYearId);
    if (campusId) params.append('campusId', campusId);

    return apiGet<RattrapageGroupInfo[]>(`/schedule/exams/rattrapage-groups?${params.toString()}`);
  }

  // Config
  async getConfig(
    academicYearId?: string,
    campusId?: string,
    filiereId?: string
  ): Promise<ExamConfig> {
    const params = new URLSearchParams();
    if (academicYearId) params.append('academicYearId', academicYearId);
    if (campusId) params.append('campusId', campusId);
    if (filiereId) params.append('filiereId', filiereId);

    return apiGet<ExamConfig>(`/schedule/exams/config?${params.toString()}`);
  }

  async updateConfig(data: Partial<ExamConfig>): Promise<ExamConfig> {
    return apiPost<ExamConfig>('/schedule/exams/config', data);
  }
}

export const examScheduleService = new ExamScheduleService();
export default examScheduleService;
